import { useEffect, useRef, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import Swal from 'sweetalert2';
import { postRequest, getRequest } from '../../../lib/api';
import { AuthContext } from '../../../authContext/context';
import {
  FormControl,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  InputLabel,
  Button,
  Grid
} from '@mui/material';
import Modal from 'react-bootstrap/Modal';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';

function MyVerticallyCenteredModal({ show, onHide, authToken, onCustomerAdd, selectedVendors = [] }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const debounceRef = useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      console.log('🔍 Customer search results:', res?.data);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const isVendorAdded = (customerId) => {
    return selectedVendors.some(c => c._id === customerId);
  };

  const handleCreateCustomer = async () => {
    if (!searchFields.email && !searchFields.phone_number) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please provide at least an email or phone number to create a customer.'
      });
      return;
    }

    try {
      setCreating(true);
      const payload = {
        title: searchFields.title || '',
        forename: searchFields.forename || '',
        surname: searchFields.surname || '',
        email: searchFields.email || '',
        phone_number: searchFields.phone_number || '',
        dob: '',
        address1: '',
        address2: '',
        town: '',
        country: '',
        postcode: ''
      };

      const res = await postRequest('private/createCustomer', payload, authToken);

      if (res?.status === 200 && res?.data) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Customer created successfully!',
          timer: 1500,
          showConfirmButton: false
        });

        onCustomerAdd(res.data);

        setSearchFields({
          title: '',
          forename: '',
          surname: '',
          email: '',
          phone_number: ''
        });

        onHide();
      } else {
        throw new Error(res?.message || 'Failed to create customer');
      }
    } catch (err) {
      console.error('Error creating customer:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Failed to create customer'
      });
    } finally {
      setCreating(false);
    }
  };

  const handleAddClick = (customer) => {
    console.log('🎯 Modal: Add button clicked for customer:', customer);
    console.log('🎯 Modal: Customer _id:', customer._id);

    onCustomerAdd(customer);

    setTimeout(() => {
      console.log('🎯 Modal: Closing modal');
      onHide();
    }, 100);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Vendor</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isVendorAdded(customer._id)}
                          onClick={() => handleAddClick(customer)}
                        >
                          {isVendorAdded(customer._id) ? 'Added' : 'Add'}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className='text-center'>
                      <Box sx={{ py: 2 }}>
                        <div style={{ marginBottom: '10px', color: '#666' }}>
                          No customers found
                        </div>
                        {(searchFields.email || searchFields.phone_number || searchFields.forename || searchFields.surname) && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleCreateCustomer}
                            disabled={creating}
                            sx={{
                              backgroundColor: '#6f42c1',
                              '&:hover': {
                                backgroundColor: '#5a32a3'
                              }
                            }}
                          >
                            {creating ? 'Creating...' : 'Add Customer'}
                          </Button>
                        )}
                      </Box>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

const EditConveyancingSaleDetails = () => {
  const { lead_id, slug } = useParams();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  
  console.log('🔄 EditConveyancingSaleDetails Component Loading...');
  console.log('🔄 Component Type: SALE DETAILS FORM (COMPLEX UI)');
  console.log('🔄 Params:', { lead_id, slug });
  console.log('🔄 Current URL:', window.location.href);

  const [selectedVendors, setSelectedVendors] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [customerError, setCustomerError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [lenderPanels, setLenderPanels] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [selectedLenderPanelId, setSelectedLenderPanelId] = useState('');
  const [leadMongoId, setLeadMongoId] = useState(null); // 🎯 Store MongoDB _id for updateConveyancingSale API

  // 🎯 Postcode validation states (same as Purchase page)
  const [postcodeMatch, setPostcodeMatch] = useState(null);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [checkingPostcode, setCheckingPostcode] = useState(false);
  const [isPostcodeValid, setIsPostcodeValid] = useState(false);

  // Info checkboxes state - only 5 fields for Sale
  const [infoOptions, setInfoOptions] = useState({
    sellingWithMortgage: false,
    arrangingIndemnityPolicy: false,
    unregisteredProperty: false,
    sharedOwnership: false,
    auctionRepossession: false
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      address1: '',
      address2: '',
      town: '',
      county: '',
      postcode: '',
      salePrice: '',
      ownershipType: 'Freehold'
      // NO completionDate for Sale
    },
  });

  // Watch postcode field for auto-validation
  const postcodeValue = watch('postcode');

  const handleCustomerAdd = (customer) => {
    console.log('➕ ========== ADDING VENDOR START ==========');
    console.log('➕ Vendor to add:', customer);
    console.log('➕ Vendor _id:', customer._id);
    console.log('➕ Current vendors count:', selectedVendors.length);
    console.log('➕ Current vendors list:', selectedVendors);

    // Check if vendor already exists
    const alreadyExists = selectedVendors.some((c) => c._id === customer._id);
    console.log('➕ Already exists?', alreadyExists);

    if (!alreadyExists) {
      // Create updated vendor list
      const updatedVendors = [...selectedVendors, customer];
      console.log('➕ New vendors count:', updatedVendors.length);
      console.log('➕ New vendors list:', updatedVendors);

      // Update state
      setSelectedVendors(updatedVendors);
      setCustomerError(false);

      console.log('✅ Vendor added successfully!');
      console.log('✅ State update called with', updatedVendors.length, 'vendors');
    } else {
      console.log('⚠️ Vendor already exists in list, not adding');
    }

    console.log('➕ ========== ADDING VENDOR END ==========');
  };

  // 🎯 Auto-validate postcode when user types (same as Purchase page)
  useEffect(() => {
    const checkPostcode = async () => {
      if (!postcodeValue || postcodeValue.trim().length < 3) {
        setPostcodeMatch(null);
        setAvailableBranches([]);
        setSelectedBranch(null);
        setIsPostcodeValid(false);
        return;
      }

      try {
        setCheckingPostcode(true);

        // Clear previous states before starting validation
        setPostcodeMatch(null);
        setAvailableBranches([]);
        setSelectedBranch(null);
        setIsPostcodeValid(false);

        // STEP 1: Check if postcode exists via checkPostcodeInBranch API
        console.log('🔍 Step 1: Checking if postcode exists');
        const checkRes = await postRequest(
          'private/checkPostcodeInBranch',
          { postcode: postcodeValue.trim() },
          authCtx.token
        );

        console.log('📡 Step 1 Result:', checkRes);

        // Check if Step 1 failed (invalid postcode)
        if (checkRes?.status === 406 || checkRes?.data?.exists === false) {
          // ❌ Step 1 Failed: Invalid postcode (not in UK postcode database)
          console.log('❌ Step 1 Failed: Postcode does not exist');
          setPostcodeMatch(null);
          setAvailableBranches([]);
          setSelectedBranch(null);
          setIsPostcodeValid(false);

          // Show error toast with API message
          Swal.fire({
            title: 'Invalid Postcode',
            text: checkRes?.message || 'This postcode does not exist in the UK postcode database. Please enter a valid UK postcode.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
          return; // Stop here, don't proceed to Step 2
        }

        // ✅ Step 1 Passed: Postcode exists
        console.log('✅ Step 1 Passed: Postcode exists');
        console.log('🔍 Step 2: Matching branch by postcode prefix');

        // STEP 2: Match branch by postcode prefix (only runs if Step 1 passed)
        const matchRes = await postRequest(
          'private/matchBranchByPostcode',
          { postcode: postcodeValue.trim() },
          authCtx.token
        );

        console.log('📡 Step 2 Result:', matchRes);

        if (matchRes?.data?.matched) {
          // ✅ Step 2 Passed: Branch matched
          console.log('✅ Step 2 Passed: Branch matched');
          setPostcodeMatch(matchRes.data.branch);
          setAvailableBranches([]);
          setSelectedBranch(null);
          setIsPostcodeValid(true);
        } else {
          // ❌ Step 2 Failed: Valid postcode but no branch serves this area
          console.log('❌ Step 2 Failed: No branch serves this postcode area');
          setPostcodeMatch(null);
          setAvailableBranches([]);
          setSelectedBranch(null);
          setIsPostcodeValid(false);
          Swal.fire({
            title: 'No Branch Coverage',
            text: 'This postcode area is not currently served by any of our branches.',
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
        }
      } catch (error) {
        console.error('Error checking postcode:', error);
        setIsPostcodeValid(false);
      } finally {
        setCheckingPostcode(false);
      }
    };

    // Debounce the API call (500ms delay after user stops typing)
    const timeoutId = setTimeout(checkPostcode, 500);
    return () => clearTimeout(timeoutId);
  }, [postcodeValue, authCtx.token]);

  const fetchLeadDetails = async () => {
    try {
      setLoading(true);
      console.log('=== AUTO-FILL DEBUG START (SALE) ===');
      console.log('Lead ID:', lead_id);
      console.log('Auth token exists:', !!authCtx.token);
      
      // Get sale edit data from dedicated API
      const res = await getRequest(`private/getEditDataSaleConveyancing/${lead_id}`, authCtx.token);
      console.log('🔍 API Response:', res);
      console.log('🔍 API Response Status:', res?.status);
      console.log('🔍 API Response Data:', res?.data);
      console.log('🔍 API Response Full:', JSON.stringify(res, null, 2));

      if (res?.status === 200 && res?.data) {
        const lead = res.data; // Backend sends { status: 200, data: editData }
        console.log('✅ SALE EDIT DATA LOADED:', lead);
        console.log('✅ Lead Property Address:', lead.property_address);
        console.log('✅ Lead Customers:', lead.customers);
        console.log('✅ Lead Partner ID:', lead.partner_id);
        console.log('✅ Lead Lender Panel ID:', lead.lender_panel_id);
        await performAutoFill(lead);
      } else {
        console.log('❌ NO LEAD DATA FOUND');
        console.log('❌ Response received:', res);
        Swal.fire('Error', 'Could not load lead data for editing', 'error');
      }
    } catch (err) {
      console.error('❌ AUTO-FILL ERROR:', err);
      Swal.fire('Error', 'Failed to load lead data: ' + err.message, 'error');
    } finally {
      setLoading(false);
      console.log('=== AUTO-FILL DEBUG END (SALE) ===');
    }
  };

  const performAutoFill = async (lead) => {
    console.log('🚀 PERFORMING SALE AUTO-FILL WITH DATA:', lead);

    // 🎯 Store MongoDB _id for updateConveyancingSale API
    if (lead._id) {
      setLeadMongoId(lead._id);
      console.log('✅ Stored MongoDB _id for Sale update:', lead._id);
    }

    try {
      // Property Address Auto-fill
      console.log('📍 Processing property_address:', lead.property_address);
      console.log('📍 Property address type:', typeof lead.property_address);
      console.log('📍 Individual address fields from backend:', {
        address1: lead.address1,
        address2: lead.address2,
        town: lead.town,
        country: lead.country,
        postcode: lead.postcode
      });
      console.log('📍 Full lead object:', lead);

      // Check if backend sent individual address fields (new format)
      if (lead.address1 || lead.address2 || lead.postcode) {
        console.log('📍 Using individual address fields from backend');
        // 🎯 FIX: Improved country/county auto-fill with comprehensive fallback including customer_ids
        const countyValue = lead.country || lead.county || lead.state ||
                           lead.property_address?.country || lead.property_address?.county ||
                           lead.customer_ids?.[0]?.address?.country || lead.customer_ids?.[0]?.address?.county ||
                           lead.vendors?.[0]?.address?.country || lead.vendors?.[0]?.address?.county ||
                           lead.customers?.[0]?.address?.country || lead.customers?.[0]?.address?.county || '';

        setValue('address1', lead.address1 || '');
        setValue('address2', lead.address2 || '');
        setValue('town', lead.town || '');
        setValue('county', countyValue);
        setValue('postcode', lead.postcode || '');
        console.log('✅ Individual address fields set from backend');
        console.log('✅ County value used:', countyValue);
        console.log('📍 Checked sources:', {
          lead_country: lead.country,
          property_address_country: lead.property_address?.country,
          customer_ids_country: lead.customer_ids?.[0]?.address?.country,
          vendors_country: lead.vendors?.[0]?.address?.country,
          customers_country: lead.customers?.[0]?.address?.country
        });
      } else if (lead.property_address) {
        if (typeof lead.property_address === 'string') {
          console.log('📍 String address detected:', lead.property_address);
          const parts = lead.property_address.split(',').map(p => p.trim());
          console.log('📍 Address parts:', parts);
          
          setValue('address1', parts[0] || '');
          setValue('address2', parts[1] || '');
          setValue('town', parts[2] || '');
          setValue('county', parts[3] || '');
          setValue('postcode', parts[parts.length - 1] || '');
          
          console.log('✅ String address fields set');
        } else if (typeof lead.property_address === 'object') {
          console.log('📍 Object address detected:', lead.property_address);
          console.log('📍 Raw property_address object:', JSON.stringify(lead.property_address, null, 2));

          const addr1 = lead.property_address.address1 || lead.property_address.line1 || '';
          const addr2 = lead.property_address.address2 || lead.property_address.line2 || '';
          const townVal = lead.property_address.town || lead.property_address.city || '';
          // 🎯 FIX: Improved country/county auto-fill with comprehensive fallback including customer_ids
          const countyVal = lead.property_address.county || lead.property_address.country || lead.property_address.state ||
                           lead.country || lead.county ||
                           lead.customer_ids?.[0]?.address?.country || lead.customer_ids?.[0]?.address?.county ||
                           lead.vendors?.[0]?.address?.country || lead.vendors?.[0]?.address?.county ||
                           lead.customers?.[0]?.address?.country || lead.customers?.[0]?.address?.county || '';
          const postcodeVal = lead.property_address.postcode || lead.property_address.postal_code || '';

          console.log('📍 Extracted values:', { addr1, addr2, townVal, countyVal, postcodeVal });

          setValue('address1', addr1);
          setValue('address2', addr2);
          setValue('town', townVal);
          setValue('county', countyVal);
          setValue('postcode', postcodeVal);

          console.log('✅ Object address fields set');
          console.log('📍 Address values confirmed:', {
            address1: addr1,
            address2: addr2,
            town: townVal,
            county: countyVal,
            postcode: postcodeVal
          });
        }
      } else if (lead.address) {
        // Fallback to address field
        console.log('📍 Using fallback address field:', lead.address);
        // 🎯 FIX: Improved country/county auto-fill with comprehensive fallback including customer_ids
        const countyValue = lead.address.country || lead.address.county ||
                           lead.country || lead.county ||
                           lead.customer_ids?.[0]?.address?.country || lead.customer_ids?.[0]?.address?.county ||
                           lead.vendors?.[0]?.address?.country || lead.vendors?.[0]?.address?.county ||
                           lead.customers?.[0]?.address?.country || lead.customers?.[0]?.address?.county || '';

        setValue('address1', lead.address.address1 || '');
        setValue('address2', lead.address.address2 || '');
        setValue('town', lead.address.town || '');
        setValue('county', countyValue);
        setValue('postcode', lead.address.postcode || '');
        console.log('✅ Fallback county value used:', countyValue);
      }

      // Sale Price Auto-fill
      const price = lead.sale_price || lead.property_value || lead.net_value || '';
      console.log('💰 Available price fields:', {
        sale_price: lead.sale_price,
        property_value: lead.property_value,
        net_value: lead.net_value
      });
      console.log('💰 Setting sale price:', price);
      setValue('salePrice', String(price));
      console.log('✅ Sale price set successfully');

      // Ownership Type Auto-fill
      const ownership = lead.ownership_type || (lead.leasehold ? 'Leasehold' : 'Freehold');
      console.log('🏡 Setting ownership type:', ownership);
      setValue('ownershipType', ownership);
      console.log('✅ Ownership type set successfully');

      // NO Completion Date for Sales

      // Vendors Auto-fill
      console.log('👥 Processing vendors...');
      console.log('👥 Available vendor/customer data:', {
        vendors: lead.vendors,
        customers: lead.customers,
        customer_ids: lead.customer_ids
      });

      let vendorsToSet = [];
      // Check for vendors first (correct field for Sale)
      if (lead.vendors && Array.isArray(lead.vendors)) {
        vendorsToSet = lead.vendors;
        console.log('✅ Using direct vendors:', vendorsToSet.length);
      }
      // Fallback to customers if vendors not found
      else if (lead.customers && Array.isArray(lead.customers)) {
        vendorsToSet = lead.customers;
        console.log('✅ Using customers as vendors:', vendorsToSet.length);
      }
      // Fetch by IDs if neither is populated
      else if (lead.customer_ids && Array.isArray(lead.customer_ids)) {
        console.log('🔍 Fetching vendors by customer IDs:', lead.customer_ids.length);

        try {
          const customerPromises = lead.customer_ids.map(async (id) => {
            console.log('🔍 Fetching vendor ID:', id);
            try {
              const customerRes = await postRequest(`private/getCustomerIndividualData/${id}`, {}, authCtx.token);
              console.log('👤 Vendor response for', id, ':', customerRes);
              return customerRes?.data || null;
            } catch (err) {
              console.log('❌ Error fetching vendor', id, ':', err);
              return null;
            }
          });

          const customers = await Promise.all(customerPromises);
          vendorsToSet = customers.filter(c => c && c._id);
          console.log('✅ Vendors fetched successfully:', vendorsToSet.length);
        } catch (err) {
          console.log('❌ Error in vendor fetching process:', err);
        }
      }

      setSelectedVendors(vendorsToSet);
      console.log('✅ Vendors state updated with', vendorsToSet.length, 'vendors');

      // Info Options Auto-fill for Sale (only 5 fields)
      console.log('📊 Processing sale info options...');
      console.log('🔍 DEBUG - Checking sellingWithMortgage from different locations:');
      console.log('  lead.selling_with_mortgage:', lead.selling_with_mortgage);
      console.log('  lead.conveyancing_details?.selling_with_mortgage:', lead.conveyancing_details?.selling_with_mortgage);
      console.log('  lead.info?.sellingWithMortgage:', lead.info?.sellingWithMortgage);
      console.log('  lead.info object:', lead.info);

      const infoData = {
        sellingWithMortgage: Boolean(
          lead.selling_with_mortgage ||
          lead.conveyancing_details?.selling_with_mortgage ||
          lead.info?.sellingWithMortgage
        ),
        arrangingIndemnityPolicy: Boolean(
          lead.arranging_indemnity_policy || 
          lead.conveyancing_details?.arranging_indemnity_policy ||
          lead.info?.arrangingIndemnityPolicy
        ),
        unregisteredProperty: Boolean(
          lead.unregistered_property || 
          lead.conveyancing_details?.unregistered_property ||
          lead.info?.unregisteredProperty
        ),
        sharedOwnership: Boolean(
          lead.shared_ownership || 
          lead.conveyancing_details?.shared_ownership ||
          lead.info?.sharedOwnership
        ),
        auctionRepossession: Boolean(
          lead.auction_repossession || 
          lead.conveyancing_details?.auction_repossession ||
          lead.info?.auctionRepossession
        )
      };
      
      console.log('📊 Processed sale info options:', infoData);
      console.log('📊 Active options:', Object.keys(infoData).filter(k => infoData[k]));
      setInfoOptions(infoData);
      console.log('✅ Info options state updated');

      // Force form update after a short delay
      setTimeout(() => {
        console.log('🔄 Forcing form update...');
        // 🎯 FIX: Use comprehensive fallback for county in reset
        const resetCountyValue = lead.country || lead.county || lead.state ||
                                lead.property_address?.country || lead.property_address?.county ||
                                lead.customer_ids?.[0]?.address?.country || lead.customer_ids?.[0]?.address?.county ||
                                lead.vendors?.[0]?.address?.country || lead.vendors?.[0]?.address?.county ||
                                lead.customers?.[0]?.address?.country || lead.customers?.[0]?.address?.county || '';

        const formValues = {
          address1: lead.address1 || lead.property_address?.address1 || lead.property_address?.line1 || (typeof lead.property_address === 'string' ? lead.property_address.split(',')[0]?.trim() : '') || '',
          address2: lead.address2 || lead.property_address?.address2 || lead.property_address?.line2 || (typeof lead.property_address === 'string' ? lead.property_address.split(',')[1]?.trim() : '') || '',
          town: lead.town || lead.property_address?.town || lead.property_address?.city || '',
          county: resetCountyValue,
          postcode: lead.postcode || lead.property_address?.postcode || lead.property_address?.postal_code || '',
          salePrice: String(price),
          ownershipType: ownership
        };
        console.log('🔄 Form values for reset:', formValues);
        console.log('🔄 Reset county value:', resetCountyValue);
        console.log('🔄 Form values stringified:', JSON.stringify(formValues, null, 2));
        reset(formValues);
        console.log('✅ Form reset completed with county:', resetCountyValue);
      }, 500);

      console.log('🎉 ========== SALE AUTO-FILL COMPLETED SUCCESSFULLY! ==========');
      console.log('📋 AUTOFILL SUMMARY:');
      // Use the same comprehensive fallback for logging
      const summaryCounty = lead.country || lead.county ||
                           lead.property_address?.country || lead.property_address?.county ||
                           lead.customer_ids?.[0]?.address?.country ||
                           lead.vendors?.[0]?.address?.country ||
                           lead.customers?.[0]?.address?.country || 'Not set';
      console.log('  📍 Address Fields:', {
        address1: lead.address1 || lead.property_address?.address1 || 'Not set',
        address2: lead.address2 || lead.property_address?.address2 || 'Not set',
        town: lead.town || lead.property_address?.town || 'Not set',
        county: summaryCounty,
        postcode: lead.postcode || lead.property_address?.postcode || 'Not set'
      });
      console.log('  💰 Sale Price:', price || 'Not set');
      console.log('  🏡 Ownership Type:', ownership || 'Not set');
      console.log('  👥 Vendors Count:', vendorsToSet.length);
      console.log('  ☑️ Info Options:', Object.keys(infoData).filter(k => infoData[k]));

      // Partner Auto-fill
      if (lead.partner_id) {
        const partnerId = typeof lead.partner_id === 'object' ? lead.partner_id._id : lead.partner_id;
        setSelectedPartnerId(partnerId);
        console.log('  🤝 Partner ID:', partnerId);
      }

      // Lender Panel Auto-fill
      if (lead.lender_panel_id) {
        const lenderPanelId = typeof lead.lender_panel_id === 'object' ? lead.lender_panel_id._id : lead.lender_panel_id;
        setSelectedLenderPanelId(lenderPanelId);
        console.log('  🏦 Lender Panel ID:', lenderPanelId);
      }

      console.log('🎉 ========================================================');
    } catch (err) {
      console.error('❌ ERROR IN PERFORM AUTO-FILL:', err);
    }
  };

  useEffect(() => {
    if (lead_id) {
      fetchLeadDetails();
    }
    fetchPartners();
    fetchLenderPanels();
  }, [lead_id]);

  const fetchPartners = async () => {
    try {
      const res = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
      if (res?.status === 200 && res?.data?.docs) {
        setPartners(res.data.docs);
        console.log('✅ Partners loaded:', res.data.docs.length);
      }
    } catch (err) {
      console.error('❌ Error fetching partners:', err);
    }
  };

  const fetchLenderPanels = async () => {
    try {
      const res = await postRequest('private/lenderPanelList', { page: 1, limit: 100 }, authCtx.token);
      if (res?.status === 200 && res?.data?.docs) {
        setLenderPanels(res.data.docs);
        console.log('✅ Lender panels loaded:', res.data.docs.length);
      }
    } catch (err) {
      console.error('❌ Error fetching lender panels:', err);
    }
  };

  // Debug useEffect to track selectedVendors changes
  useEffect(() => {
    console.log('🔄 Selected vendors state changed:', {
      count: selectedVendors.length,
      vendors: selectedVendors
    });
  }, [selectedVendors]);

  // Debug useEffect to track infoOptions changes
  useEffect(() => {
    console.log('📊 Info Options state changed:', infoOptions);
    console.log('📊 sellingWithMortgage value:', infoOptions.sellingWithMortgage);
    console.log('📊 sellingWithMortgage type:', typeof infoOptions.sellingWithMortgage);
  }, [infoOptions]);

  const onSubmit = async (formData) => {
    if (selectedVendors.length === 0) {
      setCustomerError(true);
      Swal.fire('Error', 'Please add at least one vendor', 'error');
      return;
    }

    if (!leadMongoId) {
      Swal.fire('Error', 'Lead ID not found. Please refresh and try again.', 'error');
      return;
    }

    // Block save until both postcode checks (UK database + branch match) have passed
    if (checkingPostcode) {
      Swal.fire({
        icon: 'info',
        title: 'Please Wait',
        text: 'Postcode verification is still in progress. Please try again in a moment.'
      });
      return;
    }

    if (!isPostcodeValid) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Postcode',
        text: 'Please enter a valid UK postcode that is covered by one of our branches before saving.'
      });
      return;
    }

    console.log('🚀 === SUBMITTING SALE FORM TO updateConveyancingSale API ===');
    console.log('📋 MongoDB _id:', leadMongoId);
    console.log('📋 Selected Partner ID:', selectedPartnerId);
    console.log('🏦 Selected Lender Panel ID:', selectedLenderPanelId);

    console.log('\n🔍 ========== INFO OPTIONS DEBUG ==========');
    console.log('📊 infoOptions state:', infoOptions);
    console.log('📊 sellingWithMortgage:', infoOptions.sellingWithMortgage);
    console.log('📊 Type:', typeof infoOptions.sellingWithMortgage);
    console.log('🔍 ========================================\n');

    // 🎯 Build payload for updateConveyancingSale API (matches Purchase pattern)
    const payload = {
      conveyancing_id: leadMongoId,
      vendors: selectedVendors.map((c) => c._id),
      propertyDetails: {
        address: {
          address1: formData.address1,
          address2: formData.address2,
          town: formData.town,
          county: formData.county,
          postcode: formData.postcode
        },
        propertyValue: parseFloat(formData.salePrice) || 0,
        ownershipType: formData.ownershipType
      },
      info: {
        sellingWithMortgage: infoOptions.sellingWithMortgage || false,
        arrangingIndemnityPolicy: infoOptions.arrangingIndemnityPolicy || false,
        unregisteredProperty: infoOptions.unregisteredProperty || false,
        sharedOwnership: infoOptions.sharedOwnership || false,
        auctionRepossession: infoOptions.auctionRepossession || false
      },
      partner_id: selectedPartnerId || undefined,
      lender_panel_id: selectedLenderPanelId || undefined
    };

    console.log('📦 Full Payload:', JSON.stringify(payload, null, 2));
    console.log('📦 payload.info.sellingWithMortgage:', payload.info.sellingWithMortgage);
    console.log('✅ Using updateConveyancingSale API');

    try {
      const res = await postRequest('private/updateConveyancingSale', payload, authCtx.token);
      console.log('📥 API Response:', res);

      if (res?.status === 200 || res?.status === 'success') {
        Swal.fire('Success', 'Conveyancing sale updated successfully!', 'success');
        navigate('/leads');
      } else {
        const errorMsg = res?.message || res?.data?.message || 'Update failed';
        Swal.fire('Error', errorMsg, 'error');
      }
    } catch (err) {
      console.error('❌ Error submitting Sale update:', err);
      const errorMsg = err?.response?.data?.message || err?.message || 'Something went wrong';
      Swal.fire('Error', errorMsg, 'error');
    }
  };

  const handleInfoToggle = (key) => {
    setInfoOptions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: 'Edit Conveyancing', path: '' },
        ]}
      />
      <br />

      <div className="page-header pb-3 mb-3">
        <h1 className="mt-0 mb-0" style={{ fontSize: '1.5rem', fontWeight: '500' }}>
          Edit Conveyancing - Sale #{lead_id}
        </h1>
      </div>

      <div style={{ backgroundColor: '#e9ecef', padding: '15px', borderRadius: '4px', marginBottom: '20px' }}>
        <strong>Sale Details</strong>
      </div>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="card mb-4" style={{ backgroundColor: '#ffffff' }}>
          <div className="card-body">
            {/* Property Section */}
            <h5 className="mb-4">Property</h5>
            <Row>
              <Col md={6}>
                <h6 className="mb-3">Address</h6>
              <Controller
                name="address1"
                control={control}
                rules={{ required: 'Address is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="Address Line 1"
                    fullWidth
                    sx={{ mb: 2 }}
                    error={!!errors.address1}
                    helperText={errors.address1?.message}
                  />
                )}
              />
              <Controller
                name="address2"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="Address Line 2"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
              <Controller
                name="town"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="Town/City"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
              <Controller
                name="county"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="County/State"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
              <Controller
                name="postcode"
                control={control}
                rules={{ required: 'Postcode is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="Postcode"
                    variant="outlined"
                    size="small"
                    error={!!errors.postcode}
                    helperText={errors.postcode?.message}
                  />
                )}
              />

              {/* Postcode checking indicator */}
              {checkingPostcode && postcodeValue && (
                <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
                  Checking postcode in branches...
                </Alert>
              )}

              {/* Postcode match found */}
              {postcodeMatch && !checkingPostcode && (
                <Alert severity="success" sx={{ mt: 1, fontSize: '0.875rem' }}>
                  ✓ Postcode found in branch: <strong>{postcodeMatch.name}</strong>
                </Alert>
              )}

              {/* Branch selection dropdown when postcode not found */}
              {!postcodeMatch && availableBranches.length > 0 && !checkingPostcode && (
                <Box sx={{ mt: 1 }}>
                  <Alert severity="warning" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    Postcode not found in any branch. Please select a branch:
                  </Alert>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Branch</InputLabel>
                    <Select
                      value={selectedBranch || ''}
                      onChange={(e) => {
                        const branchId = e.target.value;
                        setSelectedBranch(branchId);
                        // When user manually selects a branch, mark postcode as valid
                        if (branchId) {
                          setIsPostcodeValid(true);
                        }

                        // Find the selected branch and auto-fill postcode
                        const branch = availableBranches.find(b => b.id === branchId);
                        if (branch && branch.address.postcode) {
                          setValue('postcode', branch.address.postcode);
                        }
                      }}
                      label="Select Branch"
                    >
                      {availableBranches.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              </Col>

              <Col md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <h6>Sale Price *</h6>
                    <Controller
                      name="salePrice"
                      control={control}
                      rules={{ required: 'Sale price is required' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          fullWidth
                          placeholder="Enter sale price"
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">£</InputAdornment>,
                            }
                          }}
                          error={!!errors.salePrice}
                          helperText={errors.salePrice?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <h6>Ownership Type</h6>
                    <Controller
                      name="ownershipType"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <Select {...field} displayEmpty>
                            <MenuItem value="Freehold">Freehold</MenuItem>
                            <MenuItem value="Leasehold">Leasehold</MenuItem>
                            
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <h6>Partner</h6>
                    <FormControl fullWidth>
                      <Select
                        value={selectedPartnerId}
                        onChange={(e) => setSelectedPartnerId(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Select Partner</em>
                        </MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner._id} value={partner._id}>
                            {partner.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <h6>Lender Panel</h6>
                    <FormControl fullWidth>
                      <Select
                        value={selectedLenderPanelId}
                        onChange={(e) => setSelectedLenderPanelId(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Select Lender Panel</em>
                        </MenuItem>
                        {lenderPanels.map((lender) => (
                          <MenuItem key={lender._id} value={lender._id}>
                            {lender.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Col>
            </Row>
            
            <hr className="my-4" />
            
            {/* Vendors Section */}
            <h5 className="mb-3">Vendors</h5>
            {customerError && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                Please add at least one vendor.
              </Alert>
            )}
            <div className="mb-3">
              {selectedVendors.length > 0 ? (
                selectedVendors.map((vendor) => (
                  <div
                    key={vendor._id}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      padding: '12px 15px',
                      marginBottom: '10px',
                      position: 'relative'
                    }}
                  >
                    <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                      {vendor.title} {vendor.forename} {vendor.surname}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6c757d', display: 'flex', gap: '20px' }}>
                      <span>📧 {vendor.email}</span>
                      <span>📞 {vendor.phone_number}</span>
                    </div>
                    <CloseIcon
                      style={{
                        cursor: 'pointer',
                        color: '#dc3545',
                        position: 'absolute',
                        right: '15px',
                        top: '15px',
                        fontSize: '20px'
                      }}
                      onClick={() => setSelectedVendors(prev => prev.filter(v => v._id !== vendor._id))}
                    />
                  </div>
                ))
              ) : (
                <div style={{ color: '#6c757d', fontSize: '14px', marginBottom: '10px' }}>
                  No vendors added yet
                </div>
              )}
              <Button
                variant="text"
                onClick={() => setModalShow(true)}
                style={{ color: '#6f42c1' }}
              >
                + ADD VENDOR
              </Button>
            </div>
            
            <hr className="my-4" />
            
            {/* Info Section - Only 5 fields for Sale */}
            <h5 className="mb-3">Info</h5>
            <div className="d-flex flex-wrap gap-2">
              {Object.entries({
                sellingWithMortgage: 'Selling with Mortgage',
                arrangingIndemnityPolicy: 'Arranging an Indemnity Policy',
                unregisteredProperty: 'Unregistered Property',
                sharedOwnership: 'Shared Ownership',
                auctionRepossession: 'Auction / Repossession'
              }).map(([key, label]) => (
                <Button
                  key={key}
                  size="small"
                  variant={infoOptions[key] ? "contained" : "outlined"}
                  onClick={() => handleInfoToggle(key)}
                  style={{
                    backgroundColor: infoOptions[key] ? '#6f42c1' : 'transparent',
                    color: infoOptions[key] ? 'white' : '#6f42c1',
                    borderColor: '#6f42c1',
                    textTransform: 'none',
                    padding: '4px 12px',
                    fontSize: '13px',
                    borderRadius: '4px'
                  }}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <Row className="mb-4 mt-4">
          <Col>
            <div style={{ display: 'flex', gap: '10px' }}>
              <Button
                variant="contained"
                type="submit"
                disabled={!isPostcodeValid || checkingPostcode}
                style={{
                  backgroundColor: !isPostcodeValid || checkingPostcode ? '#cccccc' : '#6f42c1',
                  color: !isPostcodeValid || checkingPostcode ? '#666666' : 'white',
                  textTransform: 'none',
                  padding: '10px 30px'
                }}
              >
                SAVE
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                style={{
                  borderColor: '#6c757d',
                  color: '#6c757d',
                  textTransform: 'none',
                  padding: '10px 30px'
                }}
              >
                CANCEL
              </Button>
            </div>
          </Col>
        </Row>
      </Form>

      <MyVerticallyCenteredModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        authToken={authCtx.token}
        onCustomerAdd={handleCustomerAdd}
        selectedVendors={selectedVendors}
      />
    </>
  );
};

export default EditConveyancingSaleDetails;